import React from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Box, SvgIcon, theme } from '@agilize/components-core';
import styled, { css } from 'styled-components';
import { ColorsType } from '@agilize/components-core/dist/styles/theme/colors';

interface ToastIcon {
  path: string;
  fill: ColorsType;
}

export const ToastIcons = {
  warning: `${process.env.APP_PATH}public/assets/icons/unicons/exclamation-triangle.svg`,
  error: `${process.env.APP_PATH}public/assets/icons/unicons/times-circle.svg`,
  confirmation: `${process.env.APP_PATH}public/assets/icons/unicons/check-circle.svg`,
};

export const StyledToastContainer = styled(ToastContainer)`
  .Toastify__toast {
    border-radius: ${props => props.theme.forms.borderRadius}px;
    padding: ${props => props.theme.global.gutterSpace * 1.5}px;
    font-family: ${props => props.theme.global.defaultFont};
  }
  .Toastify__toast--error {
    background: ${props => props.theme.colors.red700};
  }
  .Toastify__toast--warning {
    background: ${props => props.theme.colors.yellow700};
  }
  .Toastify__toast--success {
    background: ${props => props.theme.colors.green700};
  }
`;

export const ToastBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ToastTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.global.gutterSpace * 0.75}px;
`;

export const ToastTitle = styled.h3`
  margin: 0;
  line-height: 18px;
  font-size: ${props => props.theme.fontSizes[3]};
  color: ${props => props.theme.colors.white};
  font-weight: 600;

  ${props =>
    props.type === 'default' &&
    css`
      color: ${props.theme.colors.grey900};
    `}
`;

export const ToastDescription = styled.p`
  margin: 0;
  font-size: ${props => props.theme.fontSizes[2]};
  line-height: 18px;
  font-weight: 400;

  ${props =>
    props.type === 'default' &&
    css`
      color: ${props.theme.colors.grey700};
    `}
`;

const toastBody = (
  title?: string | React.ReactNode,
  description?: string | React.ReactNode,
  type?: 'warning' | 'error' | 'confirmation' | 'default',
  actions?: React.ReactNode | React.ReactNode[],
  icon?: ToastIcon,
) => {
  const hasIcon = !!(icon || ToastIcons[type || 'default'] !== 'default');
  return (
    <Box display="flex" flex-direction="column">
      {hasIcon && <SvgIcon src={icon?.path || ToastIcons[type || 'default']} fill={icon?.fill || 'white'} width={16} height={16} />}
      <Box ml={hasIcon ? `${theme.global.gutterSpace / 2}px` : 0}>
        {title && (
          <ToastTitleContainer>
            <ToastTitle type={type}>{title}</ToastTitle>
          </ToastTitleContainer>
        )}
        {description && <ToastDescription type={type}>{description}</ToastDescription>}
        {actions && actions}
      </Box>
    </Box>
  );
};

export const toastDefault = (
  title?: string | React.ReactNode,
  description?: string | React.ReactNode,
  actions?: React.ReactNode | React.ReactNode[],
  icon?: ToastIcon,
  autoClose: false | number = 5000,
) => {
  toast(toastBody(title, description, 'default', actions, icon), {
    progressStyle: { background: theme.colors.purple400 },
    autoClose,
  });
};

export const toastSuccess = (
  title?: string | React.ReactNode,
  description?: string | React.ReactNode,
  actions?: React.ReactNode | React.ReactNode[],
  autoClose: false | number = 5000,
) => {
  toast.success(toastBody(title, description, 'confirmation', actions), { autoClose });
};

export const toastError = (
  title?: string | React.ReactNode,
  description?: string | React.ReactNode,
  actions?: React.ReactNode | React.ReactNode[],
  autoClose: false | number = 5000,
) => {
  toast.error(toastBody(title, description, 'error', actions), { autoClose });
};

export const toastWarning = (
  title?: string | React.ReactNode,
  description?: string | React.ReactNode,
  actions?: React.ReactNode | React.ReactNode[],
  autoClose: false | number = 5000,
) => {
  toast.warning(toastBody(title, description, 'warning', actions), { autoClose });
};
